import React, { ReactElement } from "react";
import "./Start.css";

type FieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  onEnter?: () => void;
  maxLength: number;
  upperCase?: boolean;
  placeholder?: string;
  big?: boolean;
};

/** A text field that trims leading spaces, collapses repeated spaces and enforces a max length. */
function Field(props: FieldProps): ReactElement {
  return (
    <div className="sp-field">
      <label htmlFor={props.id}>{props.label}</label>
      <input
        id={props.id}
        className={props.big ? "sp-input sp-input-big" : "sp-input"}
        value={props.value}
        placeholder={props.placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          let text = e.target.value.replace(/\s\s+/g, " ").replace(/^[ \t]+/, "");
          text = text.substring(0, props.maxLength);
          props.onChange(props.upperCase ? text.toUpperCase() : text);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && props.onEnter) props.onEnter();
        }}
      />
    </div>
  );
}

type StartPageProps = {
  joinLobby: string;
  joinName: string;
  joinError: string;
  createName: string;
  createError: string;
  lobbyCodeLength: number;
  canJoin: boolean;
  canCreate: boolean;
  onJoinLobbyChange: (text: string) => void;
  onJoinNameChange: (text: string) => void;
  onCreateNameChange: (text: string) => void;
  onJoin: () => void;
  onCreate: () => void;
};

/** The start page: join with a code or create a lobby, all on one screen. */
export default function StartPage(props: StartPageProps): ReactElement {
  const join = () => props.canJoin && props.onJoin();
  const create = () => props.canCreate && props.onCreate();

  return (
    <div className="sp-page">
      <div className="sp-topbar">
        <div className="sp-logo">SECRET HITLER</div>
        <div style={{ flex: "1 1 auto" }} />
        <a
          className="sp-link"
          href="https://www.secrethitler.com/assets/Secret_Hitler_Rules.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Rules (PDF)
        </a>
      </div>

      <main className="sp-main">
        <section className="sp-hero">
          <h1 className="sp-title">Find and stop Secret Hitler.</h1>
          <p className="sp-lead">
            A social deduction game for 5 to 10 players. Liberals try to pass their policies and expose
            Hitler; Fascists try to get Hitler elected. Play in the browser, no account needed.
          </p>
          <ul className="sp-points">
            <li>
              <span className="sp-dot" aria-hidden="true" />
              <div>
                <strong>Play with friends.</strong> Create a lobby and share the four-letter code or link.
              </div>
            </li>
            <li>
              <span className="sp-dot" aria-hidden="true" />
              <div>
                <strong>Short on players?</strong> Bots fill the empty seats, up to 10.
              </div>
            </li>
            <li>
              <span className="sp-dot" aria-hidden="true" />
              <div>
                <strong>Test mode.</strong> Play alone against bots and see every role, to learn or try things out.
              </div>
            </li>
          </ul>
        </section>

        <section className="sp-card" aria-label="Join or create a game">
          <div className="sp-block">
            <h2 className="sp-h2">Join a game</h2>
            <div className="sp-row">
              <Field
                id="join-lobby"
                label="Lobby code"
                value={props.joinLobby}
                onChange={props.onJoinLobbyChange}
                onEnter={join}
                maxLength={props.lobbyCodeLength}
                upperCase={true}
                placeholder="ABCD"
                big={true}
              />
              <Field
                id="join-name"
                label="Your name"
                value={props.joinName}
                onChange={props.onJoinNameChange}
                onEnter={join}
                maxLength={12}
              />
            </div>
            {props.joinError && <p className="sp-error" role="status">{props.joinError}</p>}
            <button className="gold-button gold-button-large sp-button" onClick={props.onJoin} disabled={!props.canJoin}>
              <span className="gold-label">JOIN</span>
            </button>
          </div>

          <div className="sp-or" aria-hidden="true">
            <span>or</span>
          </div>

          <div className="sp-block">
            <h2 className="sp-h2">Create a lobby</h2>
            <Field
              id="create-name"
              label="Your name"
              value={props.createName}
              onChange={props.onCreateNameChange}
              onEnter={create}
              maxLength={12}
            />
            {props.createError && <p className="sp-error" role="status">{props.createError}</p>}
            <button
              className="gold-button sp-button sp-button-secondary"
              onClick={props.onCreate}
              disabled={!props.canCreate}
            >
              <span className="gold-label">CREATE LOBBY</span>
            </button>
            <p className="sp-note">You choose Normal or Test mode and the number of bots in the lobby.</p>
          </div>
        </section>
      </main>

      <footer className="sp-footer">
        Adapted from{" "}
        <a href="https://www.secrethitler.com" target="_blank" rel="noopener noreferrer">
          Secret Hitler
        </a>{" "}
        by Goat, Wolf &amp; Cabbage (© 2016–2020) and{" "}
        <a href="https://github.com/ShrimpCryptid/Secret-Hitler-Online" target="_blank" rel="noopener noreferrer">
          Secret Hitler Online
        </a>{" "}
        by ShrimpCryptid. Licensed under{" "}
        <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noopener noreferrer">
          CC BY-NC-SA 4.0
        </a>
        . Not affiliated with or endorsed by Goat, Wolf &amp; Cabbage.
      </footer>
    </div>
  );
}
